import * as React from 'react'
import { useState } from 'react'

import { Box, Section } from '@factorialco/deprecated-design-system'
import { EmployeeCostCentersAssignmentsDocument } from 'generated/resources/graphql'
import moment from 'moment'

import { observer } from 'mobx-react'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { CostCenterAssignmentsSidepanel } from 'modules/cost_centers/components/CostCenterAssigments/AssignmentsWithSidepanel'
import { DeleteModal as DeleteCostCenterAssignmentModal } from 'modules/cost_centers/components/CostCenterAssigments/components/DeleteModal'
import useCurrentCostCentersAssignments from 'modules/cost_centers/hooks/useCurrentCostCenterAssigments'
import DimensionsAlertModal from 'modules/finance/components/Dimensions/DimensionsAlertModal'
import Loading from 'modules/finance/components/Loading'
import useTrackEmployeeChanges from 'modules/finance/hooks/useTrackEmployeeChanges'

import { CurrentAssignments } from './CurrentAssignments'
import { NoCostCentersAlert } from './NoCostCentersAlert'

interface Props {
  employeeId: string
  employeeFullName: string
  managerId: string | null
  locationId: string | null
  teamIdsSignature?: string
}

const EmployeeCostCentersAssignmentsWithHistory = ({
  employeeId,
  employeeFullName,
  managerId,
  locationId,
  teamIdsSignature = '',
}: Props) => {
  const {
    isLoading: assignmentsLoading,
    assignments,
    hasCostCenters,
    refetch,
  } = useCurrentCostCentersAssignments({
    resourceId: employeeId,
    resourceType: 'Employee',
  })

  const { data, loading: permissionsLoading } = useQuery(EmployeeCostCentersAssignmentsDocument, {
    variables: { resourceId: employeeId },
  })

  const isLoading = assignmentsLoading || permissionsLoading

  const canSeeCostCenterEmployeesAssignments =
    data?.employees.employee?._policies?.seeCostCenterMembershipsFor.can ?? false
  const canManageCostCenterEmployeesAssignments =
    data?.employees.employee?._policies?.manageCostCenterMembershipsFor.can ?? false

  const hasAssignments = assignments.length > 0

  const { navigation } = useNavigation()

  const [manageDeleteCostCenterAssignment, setManageDeleteCostCenterAssignment] = useState(false)
  const [manageCostCentersSidepanelVisible, setManageCostCentersSidepanelVisible] = useState(false)
  const [costCentersChangedAlertModalVisible, setCostCentersChangedAlertModalVisible] =
    useState(false)
  const [alertDescription, setAlertDescription] = useState('')
  const [editableAssignmentId, setEditableAssignmentId] = useState<string | undefined>(undefined)

  const assignmentsIds = assignments.map((assignment) => assignment.id)
  const currentAssignment = assignments.filter((assignment) =>
    moment(assignment.startDate).isSameOrBefore(moment())
  )

  const showAlert = (description: string) => {
    setAlertDescription(description)
    setCostCentersChangedAlertModalVisible(true)
  }

  const onClickCostCentersModal = () => {
    setManageCostCentersSidepanelVisible(true)
    track(trackingEvents.accounting.goToCostCenterManageEmployees)
  }

  const onEditAssignment = () => {
    setManageCostCentersSidepanelVisible(true)
    setEditableAssignmentId(currentAssignment[0]?.id)
  }

  const onCloseManageCostCentersSidepanel = () => {
    setEditableAssignmentId(undefined)
    setManageCostCentersSidepanelVisible(false)
  }

  const onCloseCostCentersAlertModal = () => {
    setCostCentersChangedAlertModalVisible(false)
  }

  const handleOnSubmitAlertModal = () => {
    setCostCentersChangedAlertModalVisible(false)
    setManageCostCentersSidepanelVisible(true)
  }

  const onClickDeleteOption = () => {
    setManageDeleteCostCenterAssignment(true)
  }

  useTrackEmployeeChanges({
    employeeId,
    managerId,
    locationId,
    teamIdsSignature,
    hasAssignments,
    onChange: showAlert,
  })

  const translations = canManageCostCenterEmployeesAssignments
    ? {
        description: i18n.t('finance.employee_cost_centers_info.description_manage'),
        button: i18n.t('finance.employee_cost_centers_info.manage_button'),
      }
    : {
        description: i18n.t('finance.employee_cost_centers_info.description_no_manage'),
        button: i18n.t('finance.cost_centers.assignments.view_assignments_button'),
      }

  const sectionAction = hasCostCenters
    ? {
        label: translations.button,
        onClick: onClickCostCentersModal,
      }
    : undefined

  return (
    <Loading loading={isLoading}>
      {canSeeCostCenterEmployeesAssignments && (
        <>
          <Section
            illustration="money-bag"
            title={i18n.t('finance.employee_cost_centers_info.title')}
            description={translations.description}
            action={sectionAction}
            {...(canManageCostCenterEmployeesAssignments && {
              link: {
                href: navigation.settings.finance.costCenters.fullPath(),
                label: i18n.t('finance.employee_cost_centers_info.go_to'),
              },
            })}
          >
            <Box paddingLeft="s64">
              {hasCostCenters ? (
                <CurrentAssignments
                  currentAssignment={currentAssignment}
                  assignments={assignments}
                  canManageCostCenterEmployeesAssignments={canManageCostCenterEmployeesAssignments}
                  onEditAssignment={onEditAssignment}
                  onClickDeleteOption={onClickDeleteOption}
                />
              ) : (
                <NoCostCentersAlert hasCostCenters={hasCostCenters} />
              )}
            </Box>
          </Section>
          <CostCenterAssignmentsSidepanel
            title={i18n.t(
              'finance.employee_dimensions_info.modals.set_assignments.assignment_title',
              {
                employeeName: employeeFullName,
              }
            )}
            resourceId={employeeId}
            resourceType="Employee"
            canManageAssignments={canManageCostCenterEmployeesAssignments}
            open={manageCostCentersSidepanelVisible}
            onSubmit={() => refetch()}
            onDelete={() => refetch()}
            onClose={onCloseManageCostCentersSidepanel}
            withAssignmentIdOpened={editableAssignmentId}
          />

          {costCentersChangedAlertModalVisible && (
            <DimensionsAlertModal
              description={alertDescription}
              onSubmit={handleOnSubmitAlertModal}
              onCloseDimensionsAlertModal={onCloseCostCentersAlertModal}
            />
          )}

          {manageDeleteCostCenterAssignment && (
            <DeleteCostCenterAssignmentModal
              ids={assignmentsIds}
              onClose={() => {
                setManageDeleteCostCenterAssignment(false)
              }}
              onSubmit={async () => {
                await refetch()

                setManageDeleteCostCenterAssignment(false)
              }}
            />
          )}
        </>
      )}
    </Loading>
  )
}

export default observer(EmployeeCostCentersAssignmentsWithHistory)
